import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { db, collection, getDocs, addDoc, query, where, orderBy, serverTimestamp, updateDoc, doc } from '../../components/firebase';
import { useAuth } from '../../context/AuthContext';
import {
    FiPlus, FiSearch, FiBriefcase, FiUsers, FiTrendingUp, FiBell,
    FiChevronLeft, FiChevronRight, FiMoreVertical, FiEye, FiSend, FiX, FiClock, FiTrash2, FiShare2
} from 'react-icons/fi';
import { toast } from 'react-toastify';

// Shadcn UI Components
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';

import JobPostModal from './components/JobPostModal';
import ApplicantsModal from './components/ApplicantsModal';
import JobDetailModal from './components/JobDetailModal';
import SendOfferModal from './components/SendOfferModal';

const CompanyDashboard = () => {
    const { user, userProfile } = useAuth();
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('jobs');
    const [jobs, setJobs] = useState([]);
    const [applications, setApplications] = useState([]);
    const [candidates, setCandidates] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [showListingForm, setShowListingForm] = useState(false);
    const [newJob, setNewJob] = useState({
        title: '', location: '', type: 'Full-time', description: '',
        workMode: 'Remote', stipend: '', duration: '', category: 'Engineering', skills: '',
        dateTime: '', venue: ''
    });
    const [isEditing, setIsEditing] = useState(false);
    const [editingJobId, setEditingJobId] = useState(null);
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 15;
    const [selectedCandidateForModal, setSelectedCandidateForModal] = useState(null);
    const [selectedJobForApplicants, setSelectedJobForApplicants] = useState(null);
    const [selectedJobForDetails, setSelectedJobForDetails] = useState(null);
    const [sendOfferCandidate, setSendOfferCandidate] = useState(null);
    const [sendingOffer, setSendingOffer] = useState(false);

    const fetchJobs = useCallback(async () => {
        if (!user?.uid) return;
        try {
            const q = query(collection(db, 'jobs'), where('companyId', '==', user.uid));
            const snap = await getDocs(q);
            const jobsData = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setJobs(jobsData);

            const appQ = query(collection(db, 'applications'), where('companyId', '==', user.uid));
            const appSnap = await getDocs(appQ);
            setApplications(appSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (err) {
            console.error('Error fetching jobs:', err);
        } finally {
            setLoading(false);
        }
    }, [user?.uid]);

    const fetchCandidates = useCallback(async () => {
        try {
            setLoading(true);
            const q = query(collection(db, 'Users'), orderBy('createdAt', 'desc'));
            const snap = await getDocs(q);
            setCandidates(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        } catch (err) {
            console.error('Error fetching candidates:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchNotifications = useCallback(async () => {
        if (!user?.uid) return;
        try {
            const q = query(
                collection(db, 'notifications'),
                where('recipientUid', '==', user.uid),
                orderBy('createdAt', 'desc')
            );
            const snap = await getDocs(q);
            setNotifications(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        } catch (err) {
            console.error('Error fetching notifications:', err);
        }
    }, [user?.uid]);

    useEffect(() => {
        if (user) {
            fetchJobs();
            fetchCandidates();
            fetchNotifications();
        }
    }, [user, fetchJobs, fetchCandidates, fetchNotifications]);

    const handleCreateJob = async (e) => {
        e.preventDefault();
        if (!newJob.title || !newJob.location || !newJob.description) {
            toast.error('Please fill all required fields');
            return;
        }

        try {
            if (isEditing && editingJobId) {
                const jobRef = doc(db, 'jobs', editingJobId);
                await updateDoc(jobRef, {
                    ...newJob,
                    updatedAt: serverTimestamp()
                });
                toast.success('Job updated successfully!');
            } else {
                const jobData = {
                    ...newJob,
                    companyId: user.uid,
                    companyName: userProfile?.companyName || userProfile?.fullName || 'Anonymous Company',
                    createdAt: serverTimestamp(),
                    status: 'pending'
                };
                await addDoc(collection(db, 'jobs'), jobData);

                await addDoc(collection(db, 'notifications'), {
                    title: 'New Job Posting',
                    message: `${userProfile?.companyName || userProfile?.fullName || 'A company'} posted "${newJob.title}" — awaiting approval.`,
                    type: 'info',
                    recipient: 'admin',
                    recipientUid: null,
                    timestamp: serverTimestamp(),
                    createdAt: serverTimestamp(),
                    readBy: []
                });

                toast.success('Job submitted for review!');
            }

            setShowListingForm(false);
            resetForm();
            fetchJobs();
        } catch (err) {
            toast.error(isEditing ? 'Failed to update job' : 'Failed to post job');
        }
    };

    const handleEditJob = (job) => {
        setNewJob({
            title: job.title || '',
            location: job.location || '',
            type: job.type || 'Full-time',
            description: job.description || '',
            workMode: job.workMode || 'Remote',
            stipend: job.stipend || '',
            duration: job.duration || '',
            category: job.category || 'Engineering',
            skills: job.skills || '',
            dateTime: job.dateTime || '',
            venue: job.venue || ''
        });
        setEditingJobId(job.id);
        setIsEditing(true);
        setShowListingForm(true);
    };

    const handleDeleteJob = async (jobId) => {
        if (!window.confirm('Are you sure you want to delete this job post?')) return;
        try {
            await updateDoc(doc(db, 'jobs', jobId), { status: 'deleted', deletedAt: serverTimestamp() });
            toast.success('Job post deleted');
            fetchJobs();
        } catch (err) {
            toast.error('Failed to delete job');
        }
    };

    const resetForm = () => {
        setNewJob({
            title: '', location: '', type: 'Full-time', description: '',
            workMode: 'Remote', stipend: '', duration: '', category: 'Engineering', skills: '',
            dateTime: '', venue: ''
        });
        setIsEditing(false);
        setEditingJobId(null);
    };

    const handleSendOffer = async (selectedJob, note) => {
        if (!sendOfferCandidate || !selectedJob) return;
        setSendingOffer(true);
        try {
            const candidate = sendOfferCandidate;
            const companyName = userProfile?.companyName || userProfile?.fullName || 'A Company';

            // Save to dedicated 'offers' collection
            await addDoc(collection(db, 'offers'), {
                studentId: candidate.uid || candidate.id,
                studentEmail: candidate.email,
                studentName: candidate.fullName || candidate.email,
                companyId: user.uid,
                companyName,
                jobId: selectedJob.id,
                jobTitle: selectedJob.title,
                jobType: selectedJob.type || '',
                jobLocation: selectedJob.location || '',
                jobStipend: selectedJob.stipend || '',
                note: note || '',
                status: 'pending', // pending | accepted | declined
                createdAt: serverTimestamp(),
            });

            // Send a notification to the student
            await addDoc(collection(db, 'notifications'), {
                title: '🎉 You received a Job Offer!',
                message: `${companyName} has sent you an offer for the position: "${selectedJob.title}". Check your Offers tab to respond.`,
                type: 'success',
                recipient: 'specific',
                recipientUid: candidate.uid || candidate.id,
                timestamp: serverTimestamp(),
                createdAt: serverTimestamp(),
                readBy: [],
            });

            toast.success(`✅ Offer sent to ${candidate.fullName || candidate.email}!`);
            setSendOfferCandidate(null);
            setSelectedCandidateForModal(null);
        } catch (err) {
            console.error(err);
            toast.error('Failed to send offer');
        }
        setSendingOffer(false);
    };

    const markNotificationRead = async (notif) => {
        if (notif.readBy?.includes(user.uid)) return;
        try {
            const readBy = [...(notif.readBy || []), user.uid];
            await updateDoc(doc(db, 'notifications', notif.id), { readBy });
            setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, readBy } : n));
        } catch (err) { console.error(err); }
    };

    const filteredCandidates = useMemo(() => {
        return candidates.filter(c =>
            c.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            c.collegeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            c.college?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [candidates, searchTerm]);

    const paginatedCandidates = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return filteredCandidates.slice(start, start + PAGE_SIZE);
    }, [filteredCandidates, page]);

    const applicantCountMap = useMemo(() => {
        const map = {};
        applications.forEach(app => {
            map[app.jobId] = (map[app.jobId] || 0) + 1;
        });
        return map;
    }, [applications]);

    const unreadNotifCount = notifications.filter(n => !n.readBy?.includes(user?.uid)).length;

    const stats = [
        { icon: <FiBriefcase className="h-5 w-5 text-green-500" />, label: 'Total Jobs', value: jobs.length },
        { icon: <FiUsers className="h-5 w-5 text-teal-500" />, label: 'Total Applicants', value: applications.length },
        { icon: <FiTrendingUp className="h-5 w-5 text-purple-500" />, label: 'Approved', value: jobs.filter(j => j.status === 'approved' || j.status === 'active').length },
    ];

    return (
        <div className="min-h-screen bg-background text-foreground p-4 md:p-8 pt-20 md:pt-24 transition-colors duration-300">
            <div className="max-w-7xl mx-auto space-y-8">

                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-3xl md:text-4xl font-black tracking-tight">
                            {userProfile?.companyName || userProfile?.fullName || 'Company Workspace'}
                        </h1>
                        <p className="text-muted-foreground mt-1 text-sm md:text-base">Manage your opportunities and discover top talent.</p>
                    </div>
                    <Button
                        size="lg"
                        onClick={() => setShowListingForm(true)}
                        className="font-bold flex gap-2"
                    >
                        <FiPlus className="h-5 w-5" /> Post Opportunity
                    </Button>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {stats.map((stat, i) => (
                        <Card key={i} className="hover:border-primary/50 transition-colors">
                            <CardContent className="p-6 flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-muted-foreground">{stat.label}</p>
                                    <p className="text-2xl font-bold">{stat.value}</p>
                                </div>
                                <div className="p-3 bg-secondary rounded-xl">
                                    {stat.icon}
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                {/* Main Content Tabs */}
                <Tabs defaultValue="jobs" value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className="bg-secondary p-1 mb-6 flex overflow-x-auto no-scrollbar justify-start md:inline-flex">
                        <TabsTrigger value="jobs">
                            <FiBriefcase className="mr-2 h-4 w-4" /> Job Listings
                        </TabsTrigger>
                        <TabsTrigger value="talent">
                            <FiUsers className="mr-2 h-4 w-4" /> Talent Search
                        </TabsTrigger>
                        <TabsTrigger value="notifications" className="relative">
                            <FiBell className="mr-2 h-4 w-4" /> Notifications
                            {unreadNotifCount > 0 && (
                                <Badge variant="destructive" className="ml-2 px-1.5 py-0 min-w-[1.2rem] flex justify-center text-[10px]">
                                    {unreadNotifCount}
                                </Badge>
                            )}
                        </TabsTrigger>
                    </TabsList>

                    <AnimatePresence mode="wait">
                        <TabsContent key="jobs" value="jobs">
                            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                                {loading ? (
                                    <div className="text-center py-20 text-muted-foreground">Loading your listings...</div>
                                ) : jobs.length > 0 ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {jobs.map(job => (
                                            <Card key={job.id} className="group relative overflow-hidden">
                                                <CardHeader className="pb-4">
                                                    <div className="flex justify-between items-start mb-2">
                                                        <Badge variant={job.status === 'approved' || job.status === 'active' ? 'default' : job.status === 'pending' ? 'secondary' : 'destructive'} className="uppercase text-[10px] tracking-wider font-bold">
                                                            {job.status}
                                                        </Badge>
                                                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => handleEditJob(job)}><FiPlus className="h-4 w-4" /></Button>
                                                            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => {
                                                                const url = `${window.location.origin}/job-portal/opportunities?job=${job.id}`;
                                                                if (navigator.share) {
                                                                    navigator.share({ title: `${job.title} at ${userProfile?.companyName || 'Company'}`, text: `Check out this opportunity: ${job.title}`, url }).catch(() => { });
                                                                } else {
                                                                    navigator.clipboard.writeText(url).then(() => {
                                                                        toast.success('Share link copied!');
                                                                    });
                                                                }
                                                            }}><FiShare2 className="h-4 w-4" /></Button>
                                                            <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => handleDeleteJob(job.id)}><FiTrash2 className="h-4 w-4" /></Button>
                                                        </div>
                                                    </div>
                                                    <CardTitle className="text-lg line-clamp-1 group-hover:text-primary transition-colors">{job.title}</CardTitle>
                                                    <CardDescription className="flex items-center gap-1"><FiClock className="h-3 w-3" /> {job.createdAt?.toDate().toLocaleDateString()}</CardDescription>
                                                </CardHeader>
                                                <CardContent className="space-y-4">
                                                    <div className="flex justify-between text-sm text-muted-foreground pb-4 border-b">
                                                        <span>Applicants: <strong>{applicantCountMap[job.id] || 0}</strong></span>
                                                        <span>{job.type}</span>
                                                    </div>
                                                    <div className="flex gap-2 pt-2">
                                                        <Button className="flex-1 text-xs" onClick={() => setSelectedJobForApplicants(job)}>Applicants</Button>
                                                        <Button variant="outline" className="flex-1 text-xs" onClick={() => setSelectedJobForDetails(job)}>Details</Button>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-32 border-2 border-dashed rounded-3xl space-y-4 bg-muted/20">
                                        <FiBriefcase className="mx-auto h-12 w-12 text-muted-foreground opacity-20" />
                                        <div className="space-y-1">
                                            <p className="text-lg font-bold">No jobs posted yet</p>
                                            <p className="text-sm text-muted-foreground">Start by creating your first opportunity!</p>
                                        </div>
                                        <Button variant="outline" onClick={() => setShowListingForm(true)}>Post Now</Button>
                                    </div>
                                )}
                            </motion.div>
                        </TabsContent>

                        <TabsContent key="talent" value="talent">
                            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-6">
                                <Card>
                                    <CardContent className="p-4 md:p-6">
                                        <div className="relative">
                                            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                            <Input
                                                placeholder="Search by name, email, college, or skills..."
                                                className="pl-10 h-12"
                                                value={searchTerm}
                                                onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                                            />
                                        </div>
                                    </CardContent>
                                </Card>

                                <Card>
                                    <CardContent className="p-0">
                                        <div className="overflow-x-auto">
                                            <Table>
                                                <TableHeader className="bg-secondary/50">
                                                    <TableRow>
                                                        <TableHead className="py-4">Candidate</TableHead>
                                                        <TableHead className="hidden md:table-cell">Contact</TableHead>
                                                        <TableHead className="hidden lg:table-cell">College/Branch</TableHead>
                                                        <TableHead className="text-center">Actions</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {paginatedCandidates.map((can) => (
                                                        <TableRow key={can.id} className="hover:bg-accent/40 group">
                                                            <TableCell className="py-4">
                                                                <div className="flex items-center gap-3">
                                                                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold overflow-hidden">
                                                                        {can.photo ? <img src={can.photo} alt="" className="h-full w-full object-cover" /> : (can.fullName?.charAt(0) || 'U')}
                                                                    </div>
                                                                    <div className="font-bold">{can.fullName}</div>
                                                                </div>
                                                            </TableCell>
                                                            <TableCell className="hidden md:table-cell text-muted-foreground">{can.email}</TableCell>
                                                            <TableCell className="hidden lg:table-cell">
                                                                <div className="font-semibold text-xs">{can.college || can.collegeName || 'N/A'}</div>
                                                                <div className="text-[10px] text-muted-foreground">{can.branch?.toUpperCase() || 'N/A'}</div>
                                                            </TableCell>
                                                            <TableCell className="text-center">
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    onClick={() => setSelectedCandidateForModal(can)}
                                                                    className="hover:bg-primary hover:text-white"
                                                                >
                                                                    <FiMoreVertical className="h-4 w-4" />
                                                                </Button>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                    {filteredCandidates.length === 0 && (
                                                        <TableRow>
                                                            <TableCell colSpan={4} className="text-center py-20 text-muted-foreground italic">No candidates matching your search.</TableCell>
                                                        </TableRow>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </div>
                                    </CardContent>
                                </Card>

                                {filteredCandidates.length > PAGE_SIZE && (
                                    <div className="flex items-center justify-center gap-4 mt-8">
                                        <Button variant="outline" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                                            <FiChevronLeft className="mr-2 h-4 w-4" /> Previous
                                        </Button>
                                        <div className="bg-secondary px-4 py-2 rounded-md font-bold text-sm border">
                                            Page {page} of {Math.ceil(filteredCandidates.length / PAGE_SIZE)}
                                        </div>
                                        <Button variant="outline" disabled={page >= Math.ceil(filteredCandidates.length / PAGE_SIZE)} onClick={() => setPage(p => p + 1)}>
                                            Next <FiChevronRight className="ml-2 h-4 w-4" />
                                        </Button>
                                    </div>
                                )}
                            </motion.div>
                        </TabsContent>

                        <TabsContent key="notifications" value="notifications">
                            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="max-w-3xl mx-auto space-y-3">
                                {notifications.length === 0 ? (
                                    <div className="text-center py-32 border-2 border-dashed rounded-3xl space-y-4 bg-muted/20">
                                        <FiBell className="mx-auto h-12 w-12 text-muted-foreground opacity-20" />
                                        <p className="text-lg font-bold">No notifications</p>
                                    </div>
                                ) : (
                                    notifications.map((notif, i) => {
                                        const isRead = notif.readBy?.includes(user?.uid);
                                        return (
                                            <Card key={notif.id} className={`transition-all ${isRead ? 'opacity-60 bg-muted/30' : 'border-l-4 border-l-primary shadow-md'}`} onClick={() => markNotificationRead(notif)}>
                                                <CardContent className="p-4 flex gap-4 items-start">
                                                    <div className={`mt-1.5 h-2 w-2 rounded-full flex-shrink-0 ${isRead ? 'bg-muted-foreground/30' : 'bg-primary'}`} />
                                                    <div className="flex-1 space-y-1">
                                                        <div className="flex justify-between items-start gap-2">
                                                            <p className={`font-bold leading-none ${isRead ? 'text-muted-foreground' : ''}`}>{notif.title}</p>
                                                            <span className="text-[10px] text-muted-foreground whitespace-nowrap">{notif.timestamp?.toDate().toLocaleDateString()}</span>
                                                        </div>
                                                        <p className="text-sm text-muted-foreground leading-relaxed">{notif.message}</p>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        );
                                    })
                                )}
                            </motion.div>
                        </TabsContent>
                    </AnimatePresence>
                </Tabs>
            </div>

            {/* Existing Modals kept for functional stability */}
            <JobPostModal
                show={showListingForm}
                onClose={() => { setShowListingForm(false); resetForm(); }}
                onSubmit={handleCreateJob}
                newJob={newJob}
                setNewJob={setNewJob}
                isEditing={isEditing}
            />

            <ApplicantsModal
                show={!!selectedJobForApplicants}
                onClose={() => setSelectedJobForApplicants(null)}
                job={selectedJobForApplicants}
            />

            <JobDetailModal
                show={!!selectedJobForDetails}
                onClose={() => setSelectedJobForDetails(null)}
                job={selectedJobForDetails}
            />

            {/* Talent Action Modal Re-implemented with Shadcn look */}
            <AnimatePresence>
                {selectedCandidateForModal && (
                    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            className="bg-card w-full max-w-sm rounded-3xl border shadow-2xl overflow-hidden relative"
                        >
                            <Button
                                variant="ghost"
                                size="icon"
                                className="absolute top-4 right-4 rounded-full"
                                onClick={() => setSelectedCandidateForModal(null)}
                            >
                                <FiX />
                            </Button>

                            <div className="p-8 text-center space-y-6">
                                <div className="mx-auto h-20 w-20 rounded-3xl bg-primary/10 flex items-center justify-center text-3xl font-black text-primary border shadow-sm overflow-hidden">
                                    {selectedCandidateForModal.photo ? <img src={selectedCandidateForModal.photo} alt="" className="h-full w-full object-cover" /> : (selectedCandidateForModal.fullName?.charAt(0) || 'U')}
                                </div>

                                <div className="space-y-1">
                                    <h3 className="text-xl font-black">{selectedCandidateForModal.fullName}</h3>
                                    <p className="text-sm text-muted-foreground">{selectedCandidateForModal.email}</p>
                                </div>

                                <div className="grid gap-3">
                                    <Button
                                        onClick={() => { window.open(`/portfolio/${selectedCandidateForModal.id}`, '_blank'); setSelectedCandidateForModal(null); }}
                                        className="w-full font-bold h-12"
                                    >
                                        <FiEye className="mr-2" /> View Full Profile
                                    </Button>
                                    <Button
                                        variant="outline"
                                        onClick={() => {
                                            setSendOfferCandidate(selectedCandidateForModal);
                                            setSelectedCandidateForModal(null);
                                        }}
                                        className="w-full font-bold h-12 border-primary text-primary hover:bg-primary/10"
                                    >
                                        <FiSend className="mr-2" /> Send Offer
                                    </Button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Send Offer Modal */}
            <SendOfferModal
                show={!!sendOfferCandidate}
                onClose={() => setSendOfferCandidate(null)}
                onConfirm={handleSendOffer}
                jobs={jobs}
                candidate={sendOfferCandidate}
                sending={sendingOffer}
            />
        </div >
    );
};

export default CompanyDashboard;
